<template>
  <div class="investment-card sign_up_initiated" :style="{
    backgroundImage: backgroundImage
      ? `linear-gradient(${themeName === 'dark'
        ? 'rgba(0,0,0,0.6), rgba(0,0,0,0.3)'
        : 'rgba(225,225,225,0.6), rgba(225,225,225,0.3)'
      }), url(${backgroundImage})`
      : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }" @click="handleCardClick" role="button"
    :aria-label="`Investment in ${title}, rate ${rate} p.a., click to view details`">
    <!-- Header -->
    <div class="cardHead">
      <div class="investment-card__header" :style="{
        // backgroundColor: colors.primaryBg
      }">
        <img :src="logo" class="img-fluid logo" :alt="title" />
        <span class="investment-card__title" :style="{
          // backgroundColor: colors.primaryBg,
          color: colors.primaryText,
        }" :aria-label="`Investment in ${title}`">
          {{ title }}
        </span>
        <!-- <span class="investment-card__badge" :style="{ color: badgeTextColor }">
        {{ showComingSoon ? "Coming Soon" : badge }}
      </span> -->
      </div>
    </div>

    <!-- Rate -->
    <div class="investment-card__rate" :aria-label="`Earn upto : ${rate} per annum.`">
      <div>
        <span class="earn-upto-text" :style="{ color: colors.primaryText }">
          Earn upto
        </span>
        <br />
        <span class="flex">
          <span class="highlight" :style="{ color: colors.tertiaryText }">
            {{ rate }}
          </span>
          <span :style="{ color: colors.secondaryText }">p.a.</span>
        </span>
      </div>
      <div class="flex items-start justify-end flex-col gap-2">
        <div class="badgeClass">
          <CommonWomenBadge v-if="isWomenBenefit" :text="womenInterestBenefitText" />
          <CommonSeniorCitizenBadge v-if="isSeniorCitizenBenefit" :text="seniorCitizenBenefitText" />
        </div>
      </div>
    </div>

    <!-- Features list -->
    <div class="investment-card-primary-features">
      <div v-for="(item, index) in primaryFeatures" :key="index" class="investment-card-primary-feature"
        :style="{ backgroundColor: colors.secondaryBadge }">
        <ClientOnly>
          <v-icon aria-hidden="true" v-if="item?.icon" :icon="item.icon" :style="{ color: colors.quaternaryText }"
            size="14" />
        </ClientOnly>
        <span  :aria-label="`feature ${item.text}`" :style="{ color: colors.quaternaryText }">{{ item.text
        }}</span>
      </div>
      <CommonSuccessBadge v-if="isDicgcInsured" :text="DicgcInsuredText" style="margin-bottom: 0px" borderRadius="6px"
        :noBorder="false" />
    </div>

    <!-- Secondary Features -->
    <div class="investment-card-secondary-features" :style="{
      backgroundColor:
        themeName === 'dark' ? colors.secondaryBadge : secondaryFeaturesBg,
      color: colors.primaryText,
    }">
      <template v-for="(item, index) in secondaryFeatures" :key="index">
        <div class="investment-card-secondary-feature">
          <ClientOnly>
            <v-icon aria-hidden="true" v-if="item.icon && item.isAARated" :icon="item.icon" size="21" :style="{
              color:
                themeName === 'dark'
                  ? colors.quaternaryText
                  : colors.buttonBg,
            }" />
          </ClientOnly>
          <span v-if="item.isAARated"  :aria-label="`Rating ${item.crisilRating}`">{{ item.crisilRating
          }}</span>
          <span v-if="item.minText"  :aria-label="`Min ${item.minText}`">Min: <b>{{ item.minText
          }}</b></span>
          <span v-if="item.tenure"  :aria-label="`Tenure ${item.tenure}`">Tenure: {{ item.tenure }}</span>
        </div>

        <!-- ✅ One divider only BETWEEN items -->
        <div v-if="index < secondaryFeatures.length - 1 && item?.isAARated !== false" class="divider"
          :style="{ background: colors.primaryText }"></div>
      </template>
    </div>

    <!-- Button -->
    <CommonButton class="investment-card__button sign_up_initiated" :text="buttonText" :color="buttonBg"
      :textColor="buttonTextColor" width="96px" height="36px" fontSize="12px"
      :disabled="isButtonDisable || showComingSoon" :href="investEquirizeUrl"
      :aria-label="`Know More (${title})`" @click.stop="trackFdRedirect" />
  </div>
</template>

<script setup>
import { useTheme } from "~/composables/useTheme";
import { useUtmTracking } from "~/composables/useUtmTracking";
import { useRoute } from "vue-router";
import { computed } from "vue";
import analytics, { EVENTS } from "@/services/analytics";

const { colors, themeName } = useTheme();
const { buildRedirectUrl } = useUtmTracking();
const route = useRoute();
const { investorUrl } = useInvestorPlatform();

const props = defineProps({
  title: { type: String, default: "PNB Housing Finance" },
  badge: { type: String, default: "" },
  rate: { type: String, default: "8.85%" },
  buttonText: { type: String, default: "Know More" },
  primaryFeatures: {
    type: Array,
    default: () => [
      { text: "Standard Rate" },
      { icon: "mdi-calendar", text: "11.69% Compounding Returns" },
    ],
  },
  secondaryFeatures: {
    type: Array,
    default: () => [
      { icon: "mdi-star", text: "AA+ Rated" },
      { text: "Min: ₹5,000" },
      { text: "Tenure: 5 Years" },
    ],
  },
  secondaryFeaturesBg: { type: String },
  isButtonDisable: { type: Boolean, default: false },
  buttonRedirection: { type: String, default: "" },
  showComingSoon: { type: Boolean, default: false },

  // 🎨 Dynamic props
  logo: { type: String, default: "" },
  backgroundImage: { type: String, default: "" },
  badgeBgColor: { type: String, default: "#FFF" },
  badgeTextColor: { type: String, default: "#000" },
  earnUptoTextColor: { type: String, default: "#FFF" },
  rateTextColor: { type: String, default: "#FFD700" },
  rateUnitColor: { type: String, default: "#FFF" },
  iconColor: { type: String, default: "#FFF" },
  featureTextColor: { type: String, default: "#FFF" },
  buttonBg: { type: String, default: "#002FA7" },
  buttonTextColor: { type: String, default: "#FFF" },
  isDicgcInsured: { type: Boolean, default: false },
  DicgcInsuredText: { type: String, default: "" },
  isSeniorCitizenBenefit: { type: Boolean, default: false },
  seniorCitizenBenefitText: { type: String, default: "" },
  isWomenBenefit: { type: Boolean, default: false },
  womenInterestBenefitText: { type: String, default: "" },
});

const investEquirizeUrl = computed(() => {
  const ctaName = `know-more-${props.title?.toLowerCase().replace(/\s+/g, '-') || 'finance'}-card`;
  const targetUrl = props.buttonRedirection
    ? props.buttonRedirection.startsWith("http")
      ? props.buttonRedirection
      : investorUrl(props.buttonRedirection)
    : investorUrl("/explore-products?tab=fds");

  return buildRedirectUrl(targetUrl, route.path, ctaName);
});

// Click handler for the card (button href remains untouched)
const handleCardClick = () => {
  if (!props.isButtonDisable && !props.showComingSoon) {
    trackFdRedirect()
    window.location.assign(investEquirizeUrl.value);
  }
};

function trackFdRedirect() {
  const fIssuerId = props.buttonRedirection
    ? (new URLSearchParams((props.buttonRedirection.split('?')[1]) || '')).get('fIssuerId') || undefined
    : undefined
  analytics.track(EVENTS.AUTH_REDIRECT_INITIATED, {
    user_type: 'anonymous',
    cta_name: 'fd-card',
    product: 'fd',
    fd_provider: fIssuerId,
    fd_name: props.title,
    listed_yield: Number.parseFloat(props.rate) || undefined,
  })
}
</script>

<style scoped>
.cardHead {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.investment-card {
  border: 1px solid #D5EFFF;
  background: #FFFFFF;
  width: 100%;
  position: relative;
  cursor: pointer;
  border-radius: 12px;
  /* margin: 16px 20px; */
  padding: 18px 20px;
  min-width: 300px;
  max-width: 448px;
  max-height: 230px;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /* background-color: #222; */
  /* box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2); */
}

.investment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.15);
}

.investment-card__header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  gap: 6px;
  border-radius: 4px;
}

.investment-card__title {
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

.investment-card__badge {
  font-size: 14px;
  font-weight: 600;
}

.logo {
  border-radius: 4px;
  width: 18px;
}

.earn-upto-text {
  font-size: 14px;
  font-weight: 500;
}

.investment-card__rate {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.investment-card__rate .highlight {
  font-weight: 700;
  font-size: 18px;
  display: inline-block;
  margin: 0px 4px 0px 0px;
}

.investment-card-primary-features {
  margin-top: 5px;
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 11px;
}

.investment-card-primary-feature {
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  width: max-content;
  padding: 5px;
}

.investment-card-secondary-features {
  width: fit-content;
  margin: 10px 0px;
  display: flex;
  padding: 6px;
  border-radius: 6px;
  align-items: center;
}

.investment-card-secondary-feature {
  display: flex;
  align-items: center;
  width: max-content;
  font-size: 12px;
  /* padding: 6px; */
}

.investment-card__button {
  margin-top: auto;
  display: block;
  align-self: flex-start;
}

@media (min-width: 1024px) {
  .investment-card {
    min-width: 390px;
    padding: 16px;
  }

  .earn-upto-text {
    font-size: 14px;
  }

  .investment-card__rate .highlight {
    font-size: 21px;
  }

  .investment-card-primary-feature {
    font-size: 13px;
  }

  .investment-card-secondary-feature {
    font-size: 13px;
  }
}

.badgeClass {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.divider {
  width: 2px;
  height: 15px;
  border-radius: 99px;
  margin: 0 10px;
}

.sign_up_initiated {}
</style>
